import React from 'react';
import styled, { css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 140px 0;
  background: #FFFDF7;
  @media (min-width: 768px) { padding: 180px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Layout = styled.div`
  display: grid;
  gap: 80px;
  @media (min-width: 1024px) {
    grid-template-columns: 320px 1fr;
    gap: 120px;
  }
`;

const StickyLeft = styled.div`
  @media (min-width: 1024px) {
    position: sticky;
    top: 120px;
    align-self: start;
  }
`;

const Eyebrow = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  color: #C07020;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.4rem, 4vw, 3.2rem);
  font-weight: 300;
  font-style: italic;
  color: #1A1208;
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin-bottom: 32px;
`;

const BadgeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Badge = styled.span`
  font-family: 'Raleway', serif;
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  color: #7C3A00;
  border: 1px solid rgba(124,58,0,0.25);
  padding: 6px 14px;
  border-radius: 999px;
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid rgba(26,18,8,0.1);
`;

const Para = styled.p`
  font-size: 1rem;
  font-weight: 300;
  color: rgba(26,18,8,0.75);
  line-height: 1.95;
  padding: 48px 0;
  border-bottom: 1px solid rgba(26,18,8,0.08);
  transition: opacity 0.9s ease, transform 0.9s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(16px);`}
`;

const content = {
  en: [
    "CajetaHaciendaReal LLC is a Mexican company producing traditional dairy caramel (cajeta) made from cow's milk, crafted using the same recipe that has been passed down through generations in San Juan de los Lagos, Jalisco since 1950.",
    "Our production facility is fully compliant with COFEPRIS and SENASICA regulations. We also hold FDA and D‑U‑N‑S registration, demonstrating that we meet international standards for food safety and business credibility.",
    "Our product is currently available in 700 g packaging. We are actively working toward additional certifications including HACCP, SQF, USDA Organic, Non‑GMO, and Kosher to expand our reach into premium retail channels.",
  ],
  es: [
    'CajetaHaciendaReal LLC es una empresa mexicana que produce cajeta tradicional hecha con leche de vaca, elaborada con la misma receta que se ha transmitido de generación en generación en San Juan de los Lagos, Jalisco desde 1950.',
    'Nuestra planta de producción cumple con las regulaciones de COFEPRIS y SENASICA. Contamos con registro FDA y D‑U‑N‑S, lo que demuestra que cumplimos con los estándares internacionales de inocuidad alimentaria.',
    'Nuestro producto está disponible en presentaciones de 700 g. Estamos trabajando para obtener certificaciones adicionales como HACCP, SQF, USDA Organic, Non‑GMO y Kosher para expandirnos a canales de venta premium.',
  ],
};

const badges = ['COFEPRIS', 'SENASICA', 'FDA', 'D-U-N-S'];

export function Certifications() {
  const { lang, t } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <Section id="certifications">
      <Container ref={ref} $inView={isInView}>
        <Layout>
          <StickyLeft>
            <Eyebrow>{t('Compliance', 'Cumplimiento')}</Eyebrow>
            <Title>{t('Built on trust\nand standards.', 'Construidos sobre\nconfianza y normas.')}</Title>
            <BadgeList>
              {badges.map((b) => <Badge key={b}>{b}</Badge>)}
            </BadgeList>
          </StickyLeft>
          <RightSide>
            {content[lang].map((p, i) => (
              <Para key={i} $inView={isInView} $delay={i * 120}>{p}</Para>
            ))}
          </RightSide>
        </Layout>
      </Container>
    </Section>
  );
}
